import {html, PropertyValues} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {when} from "lit/directives/when.js";
import {UuidHelper} from "../../../helpers/uuid.helper";
import {AbstractLitInputComponent} from "../../abstract-lit-input.component";
import {Entity} from "../../../domain/entities/entity";
import {ClassOf} from "../../../types/class-of.type";
import {property} from "lit/decorators/property.js";
import {container} from "tsyringe";
import {EntityManager} from "../../../persistence/entity.manager";
import {state} from "lit/decorators/state.js";
import {repeat} from "lit/directives/repeat.js";
import {styleMap} from "lit/directives/style-map.js";
import {UsedColor} from "../../../types/used-color.type";
import {autoTextColorHex} from "../../../helpers/header-item-style.helper";
import {translate} from "lit-translate";
import "../ha-input-checkbox/ha-input-checkbox.component";

@customElement(`ha-input-color`)
export class HaInputColorComponent extends AbstractLitInputComponent<string> {

    @property({type: Object})
    public entity: ClassOf<Entity> | null = null;

    /**
     * Feature plan 0074, the text colour: `null` is a value of its own — "Automatisch" — shown as a checkbox; the
     * colour picker is locked while it is on. Without `nullable` the field behaves as it always did.
     */
    @property({type: Boolean})
    public nullable = false;

    /**
     * The background the automatic text colour is computed against (the record colour in the same form): the
     * starting value when "Automatisch" is switched off without an earlier fixed value.
     */
    @property({type: String})
    public autoAgainst: string | null = null;

    private _id = UuidHelper.generateV7();

    @state()
    private _colors: UsedColor[] = [];

    /**
     * The last fixed value this field showed — kept apart from `value`, which is `null` while "Automatisch" is on,
     * so switching it off again restores the user's colour. Bound to the form instance by the dialogs
     * (`keyed(form, …)`), so it never carries over to another record.
     */
    private _lastFixed: string | null = null;

    protected willUpdate(changedProperties: PropertyValues) {
        super.willUpdate(changedProperties);
        if (changedProperties.has(`entity`) && !!this.entity && !this.nullable) {
            container.resolve(EntityManager).getRepositoryForEntity(this.entity)!.findAllColors().then(colors => this._colors = colors);
        }
        if (changedProperties.has(`value`) && this.value !== null && this.value !== ``) {
            this._lastFixed = this.value;
        }
    }

    protected shouldUpdate(_changedProperties: PropertyValues): boolean {
        return (!!this.value || this.nullable) && super.shouldUpdate(_changedProperties);
    }

    /** "Automatisch" is on exactly when the value is null — the form writes the value back on every render, an own
     *  state would jump back (feature plan 0074). */
    public get isAuto(): boolean {
        return this.nullable && (this.value === null || this.value === ``);
    }

    protected render(): unknown {

        return html`
            ${when(this.label, () => html`
                    <label for=${this._id}>${this.label}</label>
            `)}
            <div class="wrapper">
                <input type="color"
                       id=${this._id}
                       .value=${this.isAuto ? this.startValue() : this.value}
                       @input=${this._onInput}
                       ?disabled=${this.readonly || this.isAuto}
                >
                ${when(this.nullable, () => html`
                    <ha-input-checkbox class="auto-toggle"
                                       .value=${this.isAuto}
                                       label=${translate('components.ha-dialog.text-color-auto')}
                                       ?readonly=${this.readonly}
                                       @value-changed=${this._onAutoChanged}
                    ></ha-input-checkbox>
                `, () => html`
                <ul class="used-colors">
                    <div class="label_used-colors">Bereits genutzt:</div>
                    ${repeat(this._colors, c => c.color, usedColor => {
                        const colorStyle = {
                            "background-color": usedColor.color
                        }
                        return html`
                        <li class="used_colors__badge"
                            style=${styleMap(colorStyle)}
                            @click=${() => this._onChooseUsedColor(usedColor)}
                        >
                            ${usedColor.total}
                        </li>
                    `;
                    })}
                </ul>
                `)}
            </div>
            ${this._renderHelperText()}
            ${this._renderErrors()}
        `;
    }

    /** The colour "Automatisch" switches off to: the last fixed one, else what the automatic shows right now. */
    public startValue(): string {
        return this._lastFixed ?? autoTextColorHex(this.autoAgainst);
    }

    private _onAutoChanged(evt: CustomEvent<{value: boolean}>): void {
        evt.stopPropagation();
        this.value = evt.detail.value ? null : this.startValue();
        this.fire(this.value, 'value-changed');
    }

    private _onInput(evt: Event): void {
        const str: string = (evt.target as HTMLInputElement).value;
        if (str === this.value) {
            return;
        }

        this.value = str;
        this.fire(this.value, 'value-changed');
    }

    private _onChooseUsedColor(usedColor: UsedColor): void {
        if (this.value === usedColor.color) {
            return;
        }

        this.value = usedColor.color;
        this.fire(this.value, 'value-changed');
    }
}
